import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutlined";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import LinearProgress from "@mui/material/LinearProgress";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { styled } from "@mui/material/styles";
import { ConfirmResponse, useConfirmDialog } from "_shared/_providers/dialogs/confirm/confirm-dialog";
import downloadFile from "_shared/_utilities/download-file";
import Guid from "_shared/guids/guid";
import {
  useCreateDocumentsMutation,
  useGetDocumentsQuery,
  useLazyDownloadDocumentQuery,
  useUpdateDocumentMutation,
} from "documents/_api/document-api";
import { filesize } from "filesize";
import { HttpStatusCode } from "axios";
import { useGetIndividualsByUserIdsQuery } from "marketplace/individuals/_api/individual-api";
import Individual from "marketplace/individuals/individual";
import { enqueueSnackbar } from "_shared/_providers/snackbars/snackbars";
import { showErrorSnackbar, type ErrorSnackbarContext } from "_shared/_errors/error-snackbar";
import { useMemo, useRef, useState, type ChangeEvent, type DragEvent } from "react";
import { defaultStyles, FileIcon } from "react-file-icon";
import { useSession } from "users/__session/session-context";
import Document from "documents/document";
import { documentDownloadWithheldReason } from "documents/downloads/document-download-withholding";
import WorkDocumentState from "work/work-agreements/documents/_views/work-document-state";
import { NO_UPLOAD_ACCESS_TOOLTIP, UPLOAD_DOCUMENTS_ACCESS } from "permissions/access-requests/requestable-access";
import RequestAccessButton, {
  usePendingAccessRequest,
} from "permissions/access-requests/_views/request-access-button";
import useSessionUser from "permissions/session/use-session-user";
import {
  MAX_FILES_PER_WORK_DOCUMENT_UPLOAD,
  withoutWorkTopic,
  workDocumentsQuery,
  workDocumentUploadRequests,
} from "work/work-agreements/documents/_api/work-agreement-documents-api";

/** What became of one file of an upload, in the order the files were sent. */
type UploadResult = { fileName: string; added: boolean; issue?: string };

// #region Styled Components
const Panel = styled("section", {
  shouldForwardProp: (prop) => prop !== "dragging",
})<{ dragging: boolean }>(({ dragging, theme }) => ({
  display: "flex",
  flex: 1,
  flexDirection: "column",
  minHeight: 0,
  outline: dragging ? `2px dashed ${theme.palette.primary.main}` : "none",
  outlineOffset: -6,
  position: "relative",
}));
const Toolbar = styled("div")(({ theme }) => ({
  alignItems: "center",
  borderBottom: "1px solid #ddd",
  display: "flex",
  flexWrap: "wrap",
  gap: theme.spacing(1),
  justifyContent: "space-between",
  padding: theme.spacing(1, 1.5),
}));
const Hint = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  fontSize: theme.typography.caption.fontSize,
}));
const Scroller = styled("div")(() => ({
  flex: 1,
  minHeight: 0,
  overflowY: "auto",
}));
const Results = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(0.5),
  padding: theme.spacing(1, 1.5, 0),
}));
const EmptyText = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  padding: theme.spacing(4, 2),
  textAlign: "center",
}));
const DocumentIcon = styled(ListItemIcon)(({ theme }) => ({
  marginRight: theme.spacing(1),
  maxWidth: "32px",
  minWidth: "16px",
}));
const SecondaryLine = styled("span")(({ theme }) => ({
  color: theme.palette.text.secondary,
  display: "flex",
  flexWrap: "wrap",
  columnGap: theme.spacing(1),
}));
/**
 * The row of a document whose download the server would refuse. The tooltip that says why hangs from
 * this, because a disabled button fires no events; it is focusable so a keyboard reaches the tooltip.
 */
const WithheldRow = styled("span")({
  display: "block",
  width: "100%",
});
const DropNotice = styled("div")(({ theme }) => ({
  alignItems: "center",
  backgroundColor: "rgba(248, 249, 253, 0.92)",
  display: "flex",
  fontWeight: theme.typography.fontWeightMedium,
  inset: 0,
  justifyContent: "center",
  pointerEvents: "none",
  position: "absolute",
}));
// #endregion

/**
 * The types a work document may be: every type a chat attachment may be, and CSV (#6052, #6055). The
 * server decides; this only shapes the picker.
 */
export const WORK_DOCUMENT_FILE_EXTENSIONS = [
  ".pdf",
  ".doc",
  ".docx",
  ".odt",
  ".txt",
  ".csv",
  ".xls",
  ".xlsx",
  ".ods",
  ".ppt",
  ".pptx",
  ".odp",
  ".png",
  ".jpg",
  ".jpeg",
  ".gif",
  ".bmp",
  ".webp",
  ".heic",
  ".heif",
  ".svg",
  ".zip",
];


type WorkAgreementDocumentsPanelProps = {
  /**
   * The agreement's conversation: the first agreement in its revision chain. The work's documents
   * carry it in their topic, so a change order keeps the documents of the agreement it revised.
   */
  conversationId: string;
  /** The work's name, which a request for upload access names to the people who may grant it. */
  workName?: string;
  /** An archived agreement: its documents are listed and opened, and nothing is added or removed. */
  readOnly?: boolean;
};

/** What a request for upload access tells the people who may grant it the reader was doing. */
export function uploadAccessContext(workName: string | undefined): string {
  const name = workName?.trim();
  return name ? `Upload files to ${name}` : "Upload files to a work's documents";
}

/** The page the reader asked from, so a grantor can open it. */
function currentPath(): string | undefined {
  const location = globalThis.location;
  return location ? `${location.pathname}${location.search}` : undefined;
}

/**
 * The helper text for a reader who may open the work's documents but not add to them (#6595): a
 * way to ask for the access, or when they already asked.
 */
function NoUploadAccessHint(props: Readonly<{ workName: string | undefined }>) {
  const pending = usePendingAccessRequest(UPLOAD_DOCUMENTS_ACCESS);
  if (pending) {
    return (
      <Hint>
        {`You can open these. You asked for access to add files on ${pending.createdDate.toString("MMM D, YYYY")}.`}
      </Hint>
    );
  }
  return (
    <Hint>
      You can open these. To add files,{" "}
      <RequestAccessButton
        variant="link"
        label="request access"
        resource={UPLOAD_DOCUMENTS_ACCESS}
        context={uploadAccessContext(props.workName)}
        returnPath={currentPath()}
      />
      .
    </Hint>
  );
}

/**
 * The work agreement's own documents, in its Correspondence pane's Documents tab (#6054).
 *
 * Not chat attachments: these are files the people on the work keep beside it, readable by everyone
 * on it, on both sides. Files go in from the Upload button or by being dropped on the panel, many at
 * a time. Each file is answered on its own, so one refused file is reported by name and the rest
 * are still added.
 */
export default function WorkAgreementDocumentsPanel(props: Readonly<WorkAgreementDocumentsPanelProps>) {
  const { readOnly, workName } = props;
  const conversationId = Guid.fromJSON(props.conversationId)!;
  const session = useSession();
  const user = useSessionUser();
  const readerId = session.user?.id?.value;
  const confirm = useConfirmDialog();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [pageCount, setPageCount] = useState(1);
  const [dragDepth, setDragDepth] = useState(0);
  const [results, setResults] = useState<UploadResult[]>([]);

  // The first page says whether there is anything, and the last whether there is more. Each page is
  // also asked for by the rows that show it; the queries are the same, so each is fetched once.
  const documentsQuery = useWorkDocumentsPage(conversationId, 0);
  const documents = documentsQuery.data?.data ?? [];
  const lastPage = useWorkDocumentsPage(conversationId, pageCount - 1);
  const hasMore = lastPage.data?.hasNextPage ?? false;
  const [upload, uploadState] = useCreateDocumentsMutation();
  const [update] = useUpdateDocumentMutation();
  const [downloadDocument] = useLazyDownloadDocumentQuery();

  // The work being open is not enough: `Document.CreateAsync` refuses a reader without Read/Write
  // documents, so such a reader is shown the upload disabled and a way to ask for it (#6595).
  const canAdd = !readOnly && user?.canUploadDocuments() === true;
  const lacksUploadAccess = !readOnly && !canAdd;
  const dragging = canAdd && dragDepth > 0;

  async function addFiles(files: File[]) {
    if (!canAdd || files.length === 0) return;
    if (files.length > MAX_FILES_PER_WORK_DOCUMENT_UPLOAD) {
      enqueueSnackbar(`Choose no more than ${MAX_FILES_PER_WORK_DOCUMENT_UPLOAD} files at a time.`, {
        variant: "warning",
      });
      return;
    }

    const requests = workDocumentUploadRequests(files, conversationId);
    const outcome = await upload({ requests })
      .unwrap()
      .then((answers) => ({ answers, error: undefined as unknown }))
      .catch((error: unknown) => ({ answers: undefined, error }));
    if (outcome.answers) {
      // One answer per request, in the order sent.
      setResults(outcome.answers.map((answer, index) => ({
        fileName: requests[index].file.name,
        added: answer instanceof Document,
        issue: answer instanceof Document ? undefined : answer.issue,
      })));
      return;
    }
    showErrorSnackbar(outcome.error, uploadFailureContext(outcome.error));
  }

  function handleFilesChosen(event: ChangeEvent<HTMLInputElement>) {
    const files = Array.from(event.target.files ?? []);
    // Cleared, so choosing the same file again after a refusal is still a change.
    event.target.value = "";
    void addFiles(files);
  }

  function handleDragEnter(event: DragEvent<HTMLElement>) {
    if (!carriesFiles(event)) return;
    event.preventDefault();
    setDragDepth((depth) => depth + 1);
  }

  function handleDragLeave(event: DragEvent<HTMLElement>) {
    if (!carriesFiles(event)) return;
    setDragDepth((depth) => Math.max(0, depth - 1));
  }

  function handleDragOver(event: DragEvent<HTMLElement>) {
    if (!carriesFiles(event)) return;
    // Without this the browser opens the dropped file in place of the app.
    event.preventDefault();
    event.dataTransfer.dropEffect = canAdd ? "copy" : "none";
  }

  function handleDrop(event: DragEvent<HTMLElement>) {
    if (!carriesFiles(event)) return;
    event.preventDefault();
    setDragDepth(0);
    void addFiles(Array.from(event.dataTransfer.files));
  }

  async function openDocument(document: Document) {
    const outcome = await downloadDocument({ documentId: document.id.value })
      .unwrap()
      .then((response) => ({ response, error: undefined as unknown }))
      .catch((error: unknown) => ({ response: undefined, error }));
    if (outcome.response) {
      await downloadFile(outcome.response, document.name.value);
      return;
    }
    showErrorSnackbar(outcome.error, {
      doing: "opening the document",
      operation: "work-documents.open",
      refusedMessage: "You can't open this document.",
    });
  }

  async function removeDocument(document: Document) {
    const answer = await confirm({
      title: "Remove from the work?",
      message: `Everyone else on the work will no longer be able to open “${document.name.value}”. It stays in your documents.`,
      okButtonText: "Remove",
      cancelButtonText: "Cancel",
    });
    if (answer !== ConfirmResponse.Ok) return;

    // Taken off the work by taking away the work's topic; the document stays the uploader's.
    const outcome = await update({ originalDocument: document, updatedDocument: withoutWorkTopic(document, conversationId) })
      .unwrap()
      .then(() => ({ removed: true, error: undefined as unknown }))
      .catch((error: unknown) => ({ removed: false, error }));
    if (outcome.removed) return;
    showErrorSnackbar(outcome.error, {
      doing: "removing the document from the work",
      operation: "work-documents.remove",
      refusedMessage: "Only the person who added a document can take it off the work, and only while the work is open.",
    });
  }

  /** Only the uploader may take a document off the work, and not after the work has ended. */
  function canRemove(document: Document): boolean {
    return !readOnly && Boolean(readerId) && document.creatorId?.value === readerId;
  }

  const refused = results.filter((result) => !result.added);
  const addedCount = results.length - refused.length;

  return (
    <Panel
      aria-label="Documents"
      dragging={dragging}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      <Toolbar>
        {lacksUploadAccess ? (
          <NoUploadAccessHint workName={workName} />
        ) : (
          <Hint>
            {canAdd
              ? "Everyone on the work, on both sides, can open these. Drop files here, or upload them."
              : "Everyone on the work could open these. The work has ended, so nothing can be added."}
          </Hint>
        )}
        {lacksUploadAccess && (
          <Tooltip title={NO_UPLOAD_ACCESS_TOOLTIP}>
            {/* A disabled button takes no pointer events, so the span is what the tooltip listens to. */}
            <span>
              <Button size="small" variant="contained" startIcon={<UploadFileIcon />} disabled>
                Upload files
              </Button>
            </span>
          </Tooltip>
        )}
        {canAdd && (
          <>
            <Button
              size="small"
              variant="contained"
              startIcon={<UploadFileIcon />}
              disabled={uploadState.isLoading}
              onClick={() => fileInputRef.current?.click()}
            >
              Upload files
            </Button>
            <input
              ref={fileInputRef}
              data-testid="work-documents-file-input"
              type="file"
              multiple
              hidden
              accept={WORK_DOCUMENT_FILE_EXTENSIONS.join(",")}
              onChange={handleFilesChosen}
            />
          </>
        )}
      </Toolbar>
      {uploadState.isLoading && <LinearProgress aria-label="Uploading" />}
      {results.length > 0 && (
        <Results role="status">
          {addedCount > 0 && (
            <Alert severity="success" onClose={() => setResults([])}>
              {addedCount === 1 ? "1 file was added." : `${addedCount} files were added.`}
            </Alert>
          )}
          {refused.map((result, index) => (
            <Alert
              // A refusal has no id; the same name can be sent twice, so the position keeps it apart.
              key={`${result.fileName}-${index}`}
              severity="error"
              action={
                addedCount === 0 && index === 0 ? (
                  <IconButton aria-label="Dismiss" size="small" onClick={() => setResults([])}>
                    <CloseIcon fontSize="small" />
                  </IconButton>
                ) : undefined
              }
            >
              <strong>{result.fileName}</strong> was not added. {result.issue ?? "The file could not be added."}
            </Alert>
          ))}
        </Results>
      )}
      <Scroller>
        {documentsQuery.isError && <EmptyText>{listFailureMessage(documentsQuery.error)}</EmptyText>}
        {!documentsQuery.isError && documentsQuery.isLoading && <EmptyText>Loading documents…</EmptyText>}
        {!documentsQuery.isError && !documentsQuery.isLoading && documents.length === 0 && (
          <EmptyText>{emptyListText(canAdd, lacksUploadAccess)}</EmptyText>
        )}
        {documents.length > 0 && (
          <List dense aria-label="Work documents">
            {Array.from({ length: pageCount }, (_, pageIndex) => (
              <WorkDocumentRows
                key={pageIndex}
                conversationId={conversationId}
                pageIndex={pageIndex}
                readerId={readerId}
                canRemove={canRemove}
                onOpen={(document) => void openDocument(document)}
                onRemove={(document) => void removeDocument(document)}
              />
            ))}
          </List>
        )}
        {hasMore && (
          <Button fullWidth onClick={() => setPageCount((count) => count + 1)} disabled={lastPage.isFetching}>
            Show more
          </Button>
        )}
      </Scroller>
      {dragging && <DropNotice>Drop files to add them to the work</DropNotice>}
    </Panel>
  );
}

/**
 * One page of the work's documents, asked for with the same query object on every render.
 *
 * RTK Query compares a class instance by reference, so a `DocumentQuery` built during render is a
 * new argument every time, and a page whose read failed would be asked for again on every render
 * (#6488). The conversation's `Guid` is rebuilt each render too, so the query is keyed on its value.
 */
function useWorkDocumentsPage(conversationId: Guid, pageIndex: number) {
  const conversationKey = conversationId.value;
  const query = useMemo(() => workDocumentsQuery(new Guid(conversationKey), pageIndex), [conversationKey, pageIndex]);
  return useGetDocumentsQuery({ query });
}

type WorkDocumentRowsProps = {
  conversationId: Guid;
  pageIndex: number;
  readerId: string | undefined;
  canRemove: (document: Document) => boolean;
  onOpen: (document: Document) => void;
  onRemove: (document: Document) => void;
};

/** One page of the work's documents, as rows of the panel's one list. */
function WorkDocumentRows(props: Readonly<WorkDocumentRowsProps>) {
  const { conversationId, pageIndex, readerId, canRemove, onOpen, onRemove } = props;
  const page = useWorkDocumentsPage(conversationId, pageIndex);
  const documents = page.data?.data ?? [];

  const uploaderIds = [
    ...new Set(documents.map((document) => document.creatorId?.value).filter((id): id is string => Boolean(id))),
  ];
  const uploaders = useGetIndividualsByUserIdsQuery({ userIds: [...uploaderIds] }, { skip: uploaderIds.length === 0 });
  const uploaderNames = new Map(
    (uploaders.data ?? []).map((json) => [json.userId, Individual.fromJSON(json)?.fullName?.trim()]),
  );

  function uploaderLabel(document: Document): string {
    const uploaderId = document.creatorId?.value;
    if (uploaderId && uploaderId === readerId) return "Added by you";
    const name = uploaderId ? uploaderNames.get(uploaderId) : undefined;
    return name ? `Added by ${name}` : "Added by a member of the work";
  }

  return documents.map((document) => {
    // The row is the download, so a withheld download disables the row and says why (#6992).
    const withheldReason = documentDownloadWithheldReason(document);
    const row = (
      <ListItemButton disabled={Boolean(withheldReason)} onClick={() => onOpen(document)}>
        <DocumentIcon>
          <FileIcon extension={extensionOf(document.name.value)} {...fileIconStyle(document.name.value)} />
        </DocumentIcon>
        <ListItemText
          primary={document.name.value}
          secondary={
            <>
              <SecondaryLine>
                {document.fileSize !== null && document.fileSize !== undefined && (
                  <span>{filesize(document.fileSize)}</span>
                )}
                <span>{uploaderLabel(document)}</span>
                <span>{document.created?.format("MMM D, YYYY h:mm A")}</span>
              </SecondaryLine>
              {/* Under the details: why it cannot be answered from yet, or its subject matters (#6986). */}
              <WorkDocumentState document={document} />
            </>
          }
          // A div, because the chips are not phrasing content a paragraph may hold.
          slotProps={{ secondary: { component: "div" } }}
        />
      </ListItemButton>
    );
    return (
    <ListItem
      key={document.id.value}
      disablePadding
      secondaryAction={
        canRemove(document) && (
          <Tooltip title="Remove from the work">
            <IconButton
              edge="end"
              aria-label={`Remove ${document.name.value} from the work`}
              onClick={() => onRemove(document)}
            >
              <DeleteOutlineIcon />
            </IconButton>
          </Tooltip>
        )
      }
    >
      {withheldReason ? (
        <Tooltip describeChild title={withheldReason}>
          <WithheldRow tabIndex={0} aria-label={`${document.name.value}: download unavailable`}>{row}</WithheldRow>
        </Tooltip>
      ) : (
        row
      )}
    </ListItem>
    );
  });
}

/** What an empty list says: an invitation to add, unless the reader may not or the work has ended. */
function emptyListText(canAdd: boolean, lacksUploadAccess: boolean): string {
  if (canAdd) return "No documents yet. Upload files, or drop them here.";
  if (lacksUploadAccess) return "No documents yet.";
  return "No documents.";
}

/** Whether a drag carries files, rather than text or a link dragged across the panel. */
function carriesFiles(event: DragEvent<HTMLElement>): boolean {
  return Array.from(event.dataTransfer?.types ?? []).includes("Files");
}

function extensionOf(name: string | null | undefined): string {
  const dot = name?.lastIndexOf(".") ?? -1;
  return dot >= 0 && name ? name.slice(dot + 1).toLocaleLowerCase() : "";
}

function fileIconStyle(name: string | null | undefined) {
  return defaultStyles[extensionOf(name) as keyof typeof defaultStyles] ?? {};
}

export function statusOf(error: unknown): number | undefined {
  if (error && typeof error === "object" && "status" in error) {
    const status = (error as { status: unknown }).status;
    return typeof status === "number" ? status : undefined;
  }
  return undefined;
}

export function detailOf(error: unknown): string | undefined {
  if (error && typeof error === "object" && "data" in error) {
    const data = (error as { data: unknown }).data;
    if (data && typeof data === "object" && "detail" in data) {
      const detail = (data as { detail: unknown }).detail;
      return typeof detail === "string" ? detail : undefined;
    }
  }
  return undefined;
}

/**
 * What an upload the server turned down as a whole says. A refused file is reported in the results.
 * Anything this does not recognise is support's to fix: a client that could not read the answer
 * showed "could not be uploaded" over a `ZodError` and reported nothing (#6593).
 */
export function uploadFailureContext(error: unknown): ErrorSnackbarContext {
  return {
    doing: "uploading your files",
    operation: "work-documents.upload",
    message: statusOf(error) === HttpStatusCode.PayloadTooLarge
      ? detailOf(error) ?? "The files are too large to upload together. Choose fewer files."
      : undefined,
    refusedMessage: "Only members of the work can add documents to it.",
    staleMessage: "Documents cannot be added to the work now.",
  };
}

function listFailureMessage(error: unknown): string {
  return statusOf(error) === HttpStatusCode.Forbidden
    ? "Only members of the work can see its documents."
    : "The documents could not be loaded.";
}
